import { useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Calendar, Input, Tooltip } from '@flixbus/honeycomb-react';
import { Icon, IconCalendar, IconClose } from '@flixbus/honeycomb-icons-react';
import { toISODate } from '../../../../helpers/dateTime';

import { useVoucherFormContext } from '../../Context';

import { BlackoutPeriodRule } from '../../../../types/Rules';

const BlackoutFrom = ({ index }: { index: number }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const [calendarOpen, setCalendarOpen] = useState(false);

  // Get the specific blackout period rule for this index
  const ruleValue = rules.find(
    r => r.type === 'blackout_period' && (r as BlackoutPeriodRule).id === `blackout_${index}`,
  ) as BlackoutPeriodRule | undefined;

  // Get the previous blackout period rule (index - 1)
  const previousRuleValue =
    index > 0
      ? (rules.find(
          r =>
            r.type === 'blackout_period' &&
            (r as BlackoutPeriodRule).id === `blackout_${index - 1}`,
        ) as BlackoutPeriodRule | undefined)
      : undefined;

  const dateObject = ruleValue?.start ? DateTime.fromISO(ruleValue.start) : null;

  // Calculate the minimum start date: day after previous period's end, or today if no previous period
  const minDate = previousRuleValue?.end
    ? DateTime.fromISO(previousRuleValue.end)
        .plus({ days: 1 })
        .setLocale('de')
        .toFormat('yyyy-MM-dd')
    : new Date().toISOString().split('T')[0];

  const disableDate = (date: Date) => {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    return date < min;
  };

  const calendarEndDate = new Date();
  calendarEndDate.setFullYear(calendarEndDate.getFullYear() + 5);

  const handleClear = () => {
    const ruleId = `blackout_${index}`;

    // If there's no end date, remove the rule entirely
    if (!ruleValue?.end) {
      handleRuleChange({ type: 'blackout_period', id: ruleId } as BlackoutPeriodRule, true, ruleId);
    } else {
      // Keep the rule but remove the start date
      handleRuleChange(
        {
          type: 'blackout_period',
          id: ruleId,
          end: ruleValue.end,
        } as BlackoutPeriodRule,
        false,
        ruleId,
      );
    }
  };

  return (
    <Tooltip
      id={`calendar-blackout-from-tooltip-${index}`}
      active={calendarOpen}
      disableInternalHandlers
      onToggle={() => setCalendarOpen(false)}
      position='bottom'
      alignment='start'
      size='content-fit'
      wrapAround='block'
      extraClasses='calendar-tooltip'
      content={
        <Calendar
          id={`calendar-blackout-from-${index}`}
          appearance='compact'
          startDate={new Date(minDate)}
          endDate={calendarEndDate}
          selected={dateObject?.isValid ? dateObject.toJSDate() : undefined}
          showCurrentDate
          handleSelect={date => {
            const ruleId = `blackout_${index}`;
            // Update or create the rule with the new start date
            handleRuleChange(
              {
                type: 'blackout_period',
                id: ruleId,
                start: toISODate(DateTime.fromJSDate(date)),
                end: ruleValue?.end,
              } as BlackoutPeriodRule,
              false,
              ruleId,
            );
            setCalendarOpen(false);
          }}
          disableDayCondition={disableDate}
        />
      }
    >
      <Input
        name={`rule-blackout-from-input-${index}`}
        id={`rule-blackout-from-input-${index}`}
        label={<>From</>}
        type='text'
        readOnly
        required
        value={dateObject?.isValid ? dateObject.toFormat('dd.MM.yyyy') : ''}
        placeholder='DD.MM.YYYY'
        onClick={() => setCalendarOpen(prev => !prev)}
        iconLeft={
          <Button aria-label='Open calendar' onClick={() => setCalendarOpen(prev => !prev)}>
            <Icon appearance='primary' InlineIcon={IconCalendar} />
          </Button>
        }
        iconRight={
          dateObject ? (
            <Button aria-label='Clear start date field' onClick={handleClear}>
              <Icon appearance='primary' InlineIcon={IconClose} />
            </Button>
          ) : undefined
        }
      />
    </Tooltip>
  );
};

export default BlackoutFrom;
